import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const STEP_META = {
  connect_qbo:     { icon: '📊', label: 'Connect QuickBooks Online',     color: '#d97706' },
  sync_data:       { icon: '🔄', label: 'Sync your QuickBooks data',      color: '#0369a1' },
  connect_payroll: { icon: '👷', label: 'Connect payroll system',         color: '#7c3aed' },
  create_job:      { icon: '🔧', label: 'Set up your first job budget',   color: '#16a34a' },
  setup_reports:   { icon: '✉️', label: 'Set up automated email reports', color: '#1B4F72' },
};
const STEP_ORDER = ['connect_qbo', 'sync_data', 'connect_payroll', 'create_job', 'setup_reports'];

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const { data } = await api.get('/onboarding');
      setStatus(data);
      if (data.completed) setDismissed(true);
    } catch { }
  }

  async function handleDismiss(e) {
    e.stopPropagation();
    setDismissing(true);
    try {
      await api.post('/onboarding/dismiss');
      setDismissed(true);
    } catch { setDismissed(true); }
    finally { setDismissing(false); }
  }

  if (dismissed || !status) return null;

  const pct = Math.round((status.completedCount / status.totalSteps) * 100);
  const nextIncomplete = STEP_ORDER.find(s => !status.steps[s]);

  return (
    <div style={{
      background: '#fff', borderRadius: 14,
      border: '1px solid #e5e7eb',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      marginBottom: 24, overflow: 'hidden',
    }}>
      {/* Clickable header */}
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: '#1a2535', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 18 }}>⚡</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Get started with Jobhub</div>
            <div style={{ fontSize: 11, color: '#7aa3c0', marginTop: 1 }}>
              {status.completedCount} of {status.totalSteps} steps complete
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Progress ring */}
          <div style={{ position: 'relative', width: 34, height: 34 }}>
            <svg width="34" height="34" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="17" cy="17" r="13" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
              <circle cx="17" cy="17" r="13" fill="none" stroke="#4a9edd" strokeWidth="3"
                strokeDasharray={`${2 * Math.PI * 13}`}
                strokeDashoffset={`${2 * Math.PI * 13 * (1 - pct / 100)}`}
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#fff' }}>
              {pct}%
            </div>
          </div>
          <span style={{ color: '#7aa3c0', fontSize: 14 }}>{collapsed ? '▼' : '▲'}</span>
        </div>
      </div>

      {/* Thin progress bar */}
      <div style={{ height: 3, background: '#f3f4f6' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: '#4a9edd', transition: 'width 0.5s ease' }} />
      </div>

      {!collapsed && (
        <>
          {/* Steps */}
          <div style={{ padding: '6px 0' }}>
            {STEP_ORDER.map((stepId, i) => {
              const done = status.steps[stepId];
              const meta = STEP_META[stepId];
              const isNext = stepId === nextIncomplete;
              return (
                <div
                  key={stepId}
                  onClick={() => navigate('/onboarding')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 20px',
                    borderBottom: i < STEP_ORDER.length - 1 ? '1px solid #f9fafb' : 'none',
                    cursor: 'pointer', transition: 'background 0.1s',
                    background: isNext && !done ? '#fafafa' : 'transparent',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = isNext && !done ? '#fafafa' : 'transparent'}
                >
                  {/* Icon */}
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                    background: done ? '#dcfce7' : isNext ? `${meta.color}15` : '#f9fafb',
                    border: `2px solid ${done ? '#86efac' : isNext ? meta.color : '#e5e7eb'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: done ? 13 : 15, transition: 'all 0.2s',
                  }}>
                    {done ? '✓' : meta.icon}
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: isNext && !done ? 600 : 400,
                      color: done ? '#9ca3af' : '#111827',
                      textDecoration: done ? 'line-through' : 'none',
                      display: 'flex', alignItems: 'center', gap: 7,
                    }}>
                      {meta.label}
                      {isNext && !done && (
                        <span style={{ fontSize: 10, background: meta.color, color: '#fff', padding: '1px 7px', borderRadius: 20, fontWeight: 700, textDecoration: 'none' }}>Next</span>
                      )}
                      {done && (
                        <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', padding: '1px 7px', borderRadius: 20, fontWeight: 600, textDecoration: 'none' }}>Done</span>
                      )}
                    </div>
                  </div>
                  <span style={{ color: '#d1d5db', fontSize: 14, flexShrink: 0 }}>›</span>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ padding: '10px 20px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f3f4f6' }}>
            <button
              onClick={() => navigate('/onboarding')}
              style={{ padding: '7px 14px', background: '#1a2535', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              {nextIncomplete ? 'Continue setup →' : 'View setup guide →'}
            </button>
            <button
              onClick={handleDismiss}
              disabled={dismissing}
              style={{ background: 'none', border: 'none', fontSize: 12, color: '#9ca3af', cursor: 'pointer', padding: '4px 8px' }}
            >
              {dismissing ? 'Hiding...' : 'Dismiss'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}